import Button from '@atlaskit/button';
import Spinner from '@atlaskit/spinner';
import { colors } from '@atlaskit/theme';
import Tabs, { Tab, TabList, TabPanel } from '@atlaskit/tabs';

import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from 'styled-components';

import config from 'appconfig';
import { getEnvironment } from 'appconfig/environment';
import Link from 'components/common/Link';
import Table from 'components/common/Table/Table';
import { Dimensions, Font } from 'components/visuals';
import t from 'modules/i18n/intl';
import { FeatureFlags, checkFeatureGate } from 'util/statsig/index';
import TableContent, { MACTableType } from './table/TableContent';
import { StyledPipe } from './components/licensesExpandedRow/ActionsSectionElements/StyledPipe';
import { atlassianAccessKeys } from 'data/products/keys';
import MACPageHeader from './components/macPageHeader';
import GreetingMessage from './components/GreetingMessage';
import { DataCenterEOLSectionMessage } from 'components/navigation/top/WarningMessage';
import {
  selectCustomerAccountOverviews,
  selectGetCustomerPartnerId,
  selectOverviewWithOrgChildren,
  selectCustomerAccountOverviewsData,
  selectSwitchedUser,
  selectAsyncCustomerAccountOverviewsByPartnerSearchDTO,
  getMACUserChannel,
  selectEndUserCSVDownloadInProgress,
  selectEndUserCSVDownloadIsMigrationInProgress,
} from './duck/selectors';
import { fetchCustomerAccountOverviews } from './duck/actions/accountId';
import {
  CustomerAccountOverviewSearchDTO,
  MACPartnerDataType,
  OverviewEntity,
  OverviewWithOrgChildren,
} from './duck/models';
import { fetchGetCustomerByEmail } from './duck/actions/contact';
import { State } from '../../model/State';
import SearchLicensesLine from './components/SearchLicensesLine';
import { messages } from './messages';
import Pagination from '../../components/common/Pagination';
import {
  fetchDownloadCsvForEndUserLicenses,
  fetchGetAsyncCustomerAccountOverviews,
} from './duck/actions/accounts';
import { selectPartnerWhitelistStatus } from './duck/selectors/partnerMigration';
import { fetchPartnerWhitelistStatus } from './duck/actions/partnerMigration';
import {
  ENABLE_CSV_FILTERING_PARTNER_IDS_PROD,
  ENABLE_CSV_FILTERING_PARTNER_IDS_STG,
  ENABLE_PRODUCT_OFFERING_KEYMAPPINGS_PARTNER_IDS,
  MIGRATION_ENABLE_CCP_PARTNER_IDS,
} from './constants/constants';
import { fetchProductOfferingKeyMappings } from './duck/actions/product';
import { PRODUCTION } from '../../model/EnvironmentConfig';
import Tooltip from '../../components/common/Tooltip';
import { StyledLinksWrapper } from './components/styledComponents';
import YourLicensesComponent from './components/licensesExpandedRow/YourLicensesComponent';
import Loading from './components/licensesExpandedRow/LicensesSpinner';
import { MigratedCloudSubscriptionsBanner } from './components/licensesExpandedRow/DetailsRowContentElements/MigratedSubscriptionBanner';
import { selectPartnerId } from 'modules/mac/duck/selectors';
import {
  fetchDcAppParentMapping,
  uploadDcAppParentMapping,
  clearUploadDcAppParentMappingResult,
} from './duck/actions/dcAppParentMapping';
import {
  selectUploadDcAppParentMappingInProgress,
  selectUploadDcAppParentMappingResult,
} from './duck/selectors/accounts';
import {
  parseDcAppParentMappingCSV,
  downloadFailedDcAppParentMappingCSV,
} from 'util/mac/downloadCSV';
import DcAppParentMappingUploadWarning from './components/DcAppParentMappingUploadWarning';

const LicensesPageHeader = styled.h2`
  color: ${colors.N800};
  font-size: 24px;
  margin-top: 30px !important;
  font-weight: 500;
  font-family: ${Font.displayFamily};
  letter-spacing: 0.3px;
`;

const TabPanelWrapper = styled.div`
  width: 100%;
`;

const PaginationWrapper = styled.div`
  margin-top: 10px;
`;

const DownloadLicensesWrapper = styled.div`
  display: flex;
  margin-top: 10px;
  align-items: baseline;
`;

const SpinnerWrapper = styled.span`
  margin-right: -${Dimensions.unit}px;
`;

const tableHeadersEndUserLicenses = [
  {
    display: 'SEN',
    key: 'licenses-sen',
    width: 15,
  },
  {
    display: 'Product',
    key: 'licenses-product',
    width: 20,
  },
  {
    display: 'Organization',
    key: 'licenses-organization',
    width: 15,
  },
  {
    display: 'Main Contact',
    key: 'licenses-main-contact',
    width: 15,
  },
  {
    display: 'End Date',
    key: 'licenses-end-date',
    width: 10,
  },
  {
    display: 'Last transaction',
    key: 'licenses-last-transaction',
    width: 20,
  },
];

const isPartnerWhitelistedForMigrationCSVDownload = (partnerId: string | undefined) =>
  partnerId ? MIGRATION_ENABLE_CCP_PARTNER_IDS.includes(Number(partnerId)) : false;

const isWhitelistedForProductOfferingCSVDownload = (partnerId: string | undefined) =>
  partnerId ? ENABLE_PRODUCT_OFFERING_KEYMAPPINGS_PARTNER_IDS.includes(Number(partnerId)) : false;

const overrideAccessAccountIds = (items: OverviewEntity[]): any =>
  items.map((account) => {
    if (atlassianAccessKeys.includes(account.productKey)) {
      return {
        ...account,
        accountId: account.parentAccountId,
      };
    }
    return account;
  });

const removeOrgChildren = (items: OverviewEntity[]): any =>
  items.filter(
    (acc) => acc.parentAccountId === null || atlassianAccessKeys.includes(acc.productKey),
  );

export type LicensesPaginationProps = { maxResult: number; offset: number; totalItems: number };

type Props = {
  senNumber?: string;
  partnerData?: any;
  pagination: LicensesPaginationProps;
  onPaginationChange: (
    params: Partial<Omit<LicensesPaginationProps, 'totalItems' | 'maxResult'>>,
  ) => void;
  updateSearchParams: (
    searchObject:
      | CustomerAccountOverviewSearchDTO
      | ((prevVar: CustomerAccountOverviewSearchDTO) => CustomerAccountOverviewSearchDTO),
  ) => void;
  searchParams: CustomerAccountOverviewSearchDTO;
  cartEnableCCPSearchCallFGOn?: boolean;
};

const FAQLink = (chunks) => <a href={config.wacPurchaseLicensingUrl}>{chunks}</a>;

const supportLink = (chunks) => (
  <a href={config.support} target="_blank" rel="noopener noreferrer">
    {chunks}
  </a>
);

const marketplaceLink = (chunks) => <a href={config.marketplaceUrl}>{chunks}</a>;

const questionsLink = (chunks) => <a href={config.companyContact}>{chunks}</a>;

export const LicensesPage = (props: Props) => {
  const dispatch = useDispatch();

  const cmtLinkEnabled = checkFeatureGate(FeatureFlags.CLOUD_MIGRATION_TRIAL_LINK);
  const [endUserLicensesToDisplay, setEndUserLicensesToDisplay] = useState<Array<OverviewEntity>>(
    [],
  );
  const [yourLicensesToDisplay, setYourLicensesToDisplay] = useState<
    Array<OverviewWithOrgChildren>
  >([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [preselectedLicenseBySen, setPreselectedLicenseBySen] = useState<
    OverviewEntity | undefined
  >(undefined);
  const [shouldShowPagination, setShouldShowPagination] = useState(false);
  const [endUserLicensesTabIsChosen, setEndUserLicensesTabIsChosen] = useState(false);
  const [shouldDisplayEndUserLicensesTab, setShouldDisplayEndUserLicensesTab] = useState(false);
  const [yourLicensesItems, setYourLicensesItems] = useState<OverviewEntity[]>([]);
  const [defaultLicensesItems, setDefaultLicensesItems] = useState<OverviewEntity[]>([]);
  const [isCsvDownloadMigration, setCsvDownloadMigration] = useState(false);

  const endUserLicensesItems = useSelector(selectCustomerAccountOverviews);
  const overviewWithOrgChildrenItems = useSelector(selectOverviewWithOrgChildren);
  const isLicensesLoading = useSelector(
    (state) =>
      (state as State).mac.accountId.customerAccountOverviews?.inProgress ||
      (state as State).mac.accountId.overviewWithOrgChildren?.inProgress,
  );
  const pollingUuid = useSelector(selectAsyncCustomerAccountOverviewsByPartnerSearchDTO);
  const isPartnerWhitelistStatusMigration =
    useSelector(selectPartnerWhitelistStatus)?.results?.length > 0;
  const { isPartner } = useSelector((state: State) => getMACUserChannel(state));
  const endUserCSVDownloadInProgress = useSelector(selectEndUserCSVDownloadInProgress);
  const endUserCSVDownloadIsMigrationInProgress = useSelector(
    selectEndUserCSVDownloadIsMigrationInProgress,
  );
  const partnerId = useSelector(selectPartnerId);
  const isUploadDcAppParentMappingInProgress = useSelector(
    selectUploadDcAppParentMappingInProgress,
  );
  const uploadDcAppParentMappingResult = useSelector(selectUploadDcAppParentMappingResult);

  const displayMigrationCsvDownloadLink = props.cartEnableCCPSearchCallFGOn
    ? isPartnerWhitelistStatusMigration
    : isPartnerWhitelistedForMigrationCSVDownload(props.partnerData?.id);

  const displayProductOfferingKeyLink =
    isWhitelistedForProductOfferingCSVDownload(props.partnerData?.id) ||
    (props.partnerData?.partner && getEnvironment() !== PRODUCTION);

  const displayDcParentAndAppsMappingLink = checkFeatureGate(
    FeatureFlags.CART_MAPPING_PARENT_APP_CSV,
  );

  useEffect(() => {
    setShouldDisplayEndUserLicensesTab(props.partnerData?.id && props.partnerData?.partner);
  }, [props.partnerData]);

  useEffect(() => {
    setDefaultLicensesItems(
      overrideAccessAccountIds(overviewWithOrgChildrenItems as OverviewEntity[]),
    );
  }, [overviewWithOrgChildrenItems]);

  useEffect(() => {
    setEndUserLicensesTabIsChosen(shouldDisplayEndUserLicensesTab && selectedTab === 0);
  }, [shouldDisplayEndUserLicensesTab, selectedTab]);

  useEffect(() => {
    setYourLicensesItems(removeOrgChildren(defaultLicensesItems));
  }, [defaultLicensesItems]);

  useEffect(() => {
    const licensesToFilter = endUserLicensesTabIsChosen ? endUserLicensesItems : yourLicensesItems;
    const foundLicense = licensesToFilter.find(
      (license) =>
        license.accountId?.toString() === props.senNumber ||
        license.licenseId?.toString() === props.senNumber?.replace('L', ''),
    );
    if (foundLicense) {
      setPreselectedLicenseBySen(foundLicense as OverviewEntity);
    }
  }, [yourLicensesItems.length, endUserLicensesItems.length, endUserLicensesTabIsChosen]);

  useEffect(() => {
    if (pollingUuid && props?.partnerData?.id) {
      dispatch(
        fetchDownloadCsvForEndUserLicenses(
          pollingUuid,
          isCsvDownloadMigration,
          props.partnerData.id,
        ),
      );
    }
  }, [pollingUuid, props?.partnerData?.id]);

  useEffect(() => {
    setShouldShowPagination(
      endUserLicensesTabIsChosen && props.pagination?.maxResult < props.pagination?.totalItems,
    );
  }, [endUserLicensesTabIsChosen, props.pagination]);

  const handleDownloadLicenseOverviewCSV = (isMigration = false) => {
    if (isMigration) {
      setCsvDownloadMigration(isMigration);
    }

    const shouldAllowCSVFiltering =
      process.env.NODE_ENV === 'production'
        ? ENABLE_CSV_FILTERING_PARTNER_IDS_PROD.includes(+props.partnerData.id)
        : ENABLE_CSV_FILTERING_PARTNER_IDS_STG.includes(+props.partnerData.id);

    const searchObject = shouldAllowCSVFiltering
      ? { ...props.searchParams, migration: isMigration }
      : { partnerId: props.partnerData.id, migration: isMigration };

    dispatch(fetchGetAsyncCustomerAccountOverviews(searchObject));
  };

  const handleDownloadProductOfferingKey = () => {
    dispatch(fetchProductOfferingKeyMappings());
  };

  const handleDownloadDcParentAndAppsMapping = () => {
    if (!partnerId) {
      return;
    }
    dispatch(fetchDcAppParentMapping(partnerId));
  };

  const handleUploadDcParentAndAppsMapping = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!partnerId) return;
    const file = event.target.files?.[0];
    if (!file) return;

    dispatch(clearUploadDcAppParentMappingResult());

    const reader = new FileReader();
    reader.onload = (e) => {
      const csvText = e.target?.result as string;
      if (!csvText) return;
      const mappings = parseDcAppParentMappingCSV(csvText);
      if (mappings.length > 0) {
        dispatch(uploadDcAppParentMapping(partnerId, mappings));
      }
    };
    reader.readAsText(file);
    // Reset the input so the same file can be re-uploaded if needed
    event.target.value = '';
  };

  return (
    <>
      <MACPageHeader />
      <GreetingMessage />
      <DataCenterEOLSectionMessage />
      <section className="licenses-support-section">
        <LicensesPageHeader>{t('navigation.header.support')}</LicensesPageHeader>
        <ul>
          <li>
            {t(messages.licensesSeeFAQinformation, {
              link: FAQLink,
            })}
          </li>
          <li>
            {t(messages.licensesVisitSupportInformation, {
              link: supportLink,
            })}
          </li>
          <li>
            {t(messages.licensesInformationMarketplace, {
              link: marketplaceLink,
            })}
          </li>
        </ul>
        <br />
        {t(messages.licensesGotMoreQuestions, {
          link: questionsLink,
        })}
      </section>
      <LicensesPageHeader>{t('mac.licenses.title')}</LicensesPageHeader>
      <MigratedCloudSubscriptionsBanner shouldShowPPCLink={endUserLicensesTabIsChosen} />
      <StyledLinksWrapper>
        {shouldDisplayEndUserLicensesTab && (
          <>
            <Button
              appearance="link"
              href="https://atlassianpartners.atlassian.net/support?postauth=true#/"
            >
              {t('mac.partner.support.link')}
            </Button>
            <StyledPipe />
          </>
        )}
        {cmtLinkEnabled ? (
          <>
            <Tooltip
              useHover={false}
              direction="up-middle"
              content={t('mac.new.cmt.license.tooltip')}
            >
              <Button appearance="link">
                <Link to="/migration/assess/cloud-migration-trial/options">
                  {t('mac.new.cmt.license')}
                </Link>
              </Button>
            </Tooltip>
            <StyledPipe />
            <Tooltip
              useHover={false}
              direction="up-middle"
              content={t('mac.explore.cloud.pricing.tooltip')}
            >
              <Button appearance="link" href={config.calculatePricingURL}>
                {t('mac.explore.cloud.pricing')}
              </Button>
            </Tooltip>
          </>
        ) : (
          <>
            <Button appearance="link" href={config.calculatePricingURL}>
              {t('mac.explore.cloud.pricing')}
            </Button>
          </>
        )}
      </StyledLinksWrapper>
      {shouldDisplayEndUserLicensesTab ? (
        <Tabs
          onChange={(index) => setSelectedTab(index)}
          selected={selectedTab}
          id="licenses-page-tabs"
        >
          <TabList>
            <Tab>{t('mac.licenses.tab-title.user-end.licenses')}</Tab>
            <Tab>{t('mac.licenses.tab-title.licenses')}</Tab>
          </TabList>
          <TabPanel>
            <TabPanelWrapper>
              <SearchLicensesLine
                allLicenses={endUserLicensesItems}
                updateLicensesToDisplay={setEndUserLicensesToDisplay}
                updateSearchParams={props.updateSearchParams}
                searchParams={props.searchParams}
                isPartner
                partnerId={props?.partnerData?.id}
                senNumber={props?.senNumber}
                preselectedLicenseBySen={preselectedLicenseBySen}
              />
              <Table
                body={
                  !isLicensesLoading ? (
                    <TableContent
                      items={endUserLicensesToDisplay}
                      tableType={MACTableType.licenses}
                      senNumber={props.senNumber}
                      endUserView
                      isPartner={isPartner}
                    />
                  ) : (
                    <Loading endUserLicensesTabIsChosen />
                  )
                }
                footers={[]}
                headers={tableHeadersEndUserLicenses}
                dataAttr="licenses"
              />
            </TabPanelWrapper>
          </TabPanel>
          <TabPanel>
            <TabPanelWrapper>
              <YourLicensesComponent
                licenseItems={yourLicensesItems}
                setLicensesToDisplay={setYourLicensesToDisplay}
                updateSearchParams={props.updateSearchParams}
                partnerData={props.partnerData?.id}
                senNumber={props.senNumber}
                preselectedLicenseBySen={preselectedLicenseBySen}
                isLicensesLoading={isLicensesLoading}
                licensesToDisplay={yourLicensesToDisplay}
                isPartner={isPartner}
              />
            </TabPanelWrapper>
          </TabPanel>
        </Tabs>
      ) : (
        <YourLicensesComponent
          licenseItems={yourLicensesItems}
          setLicensesToDisplay={setYourLicensesToDisplay}
          updateSearchParams={props.updateSearchParams}
          partnerData={props.partnerData?.id}
          senNumber={props.senNumber}
          preselectedLicenseBySen={preselectedLicenseBySen}
          isLicensesLoading={isLicensesLoading}
          licensesToDisplay={yourLicensesToDisplay}
          isPartner={isPartner}
        />
      )}
      {shouldShowPagination && (
        <PaginationWrapper data-cy="pagination">
          <Pagination
            limit={props.pagination.maxResult}
            offset={props.pagination.offset}
            totalItems={props.pagination.totalItems}
            onPaginationChange={props.onPaginationChange}
          />
        </PaginationWrapper>
      )}
      {endUserLicensesTabIsChosen ? (
        <>
          <DownloadLicensesWrapper>
            {endUserCSVDownloadInProgress && (
              <SpinnerWrapper>
                <Spinner size="small" />
              </SpinnerWrapper>
            )}
            <Button
              appearance="link"
              onClick={() => handleDownloadLicenseOverviewCSV()}
              isDisabled={endUserCSVDownloadInProgress}
            >
              {t('mac.licenses.end-user.download-csv')}
            </Button>
            {getEnvironment() !== PRODUCTION || displayMigrationCsvDownloadLink ? (
              <>
                {endUserCSVDownloadIsMigrationInProgress && (
                  <SpinnerWrapper>
                    <Spinner size="small" />
                  </SpinnerWrapper>
                )}
                <Button
                  appearance="link"
                  onClick={() => handleDownloadLicenseOverviewCSV(true)}
                  isDisabled={endUserCSVDownloadIsMigrationInProgress}
                >
                  {t('mac.licenses.end-user.download-csv.migration')}
                </Button>
              </>
            ) : null}
            {displayProductOfferingKeyLink && (
              <Button appearance="link" onClick={() => handleDownloadProductOfferingKey()}>
                {t('mac.licenses.end-user.product.offering.key.mapping')}
              </Button>
            )}
          </DownloadLicensesWrapper>
          {displayDcParentAndAppsMappingLink && (
            <>
              <DownloadLicensesWrapper>
                <Button appearance="link" onClick={() => handleDownloadDcParentAndAppsMapping()}>
                  {t(messages.dcAppParentMappingDownload)}
                </Button>
                {isUploadDcAppParentMappingInProgress && (
                  <SpinnerWrapper>
                    <Spinner size="small" />
                  </SpinnerWrapper>
                )}
                <Button
                  appearance="link"
                  isDisabled={isUploadDcAppParentMappingInProgress}
                  onClick={() => {
                    document.getElementById('dc-app-parent-mapping-upload')?.click();
                  }}
                >
                  {t(messages.dcAppParentMappingUpload)}
                </Button>
                <input
                  id="dc-app-parent-mapping-upload"
                  type="file"
                  accept=".csv"
                  style={{ display: 'none' }}
                  onChange={handleUploadDcParentAndAppsMapping}
                />
              </DownloadLicensesWrapper>
              {uploadDcAppParentMappingResult && (
                <DcAppParentMappingUploadWarning
                  uploadResult={uploadDcAppParentMappingResult}
                  onDownloadFailed={() => {
                    if (!partnerId) return;
                    downloadFailedDcAppParentMappingCSV(
                      uploadDcAppParentMappingResult.failedMappings,
                      partnerId,
                    );
                  }}
                />
              )}
            </>
          )}
        </>
      ) : null}
    </>
  );
};

export const MACLicensesPage = (props) => {
  const dispatch = useDispatch();
  const cartEnableCCPSearchCall = checkFeatureGate(
    FeatureFlags.CART_MAC_MIGRATION_ENABLE_CCP_SEARCH_CALL,
  );
  const [pagination, setPagination] = useState<LicensesPaginationProps>({
    maxResult: 15,
    offset: 0,
    totalItems: 15,
  });
  const [searchData, setSearchData] = useState<CustomerAccountOverviewSearchDTO>({ partnerId: 0 });
  const totalResults = useSelector(
    // @ts-expect-error TODO: Fix selector type
    (state) => selectCustomerAccountOverviewsData(state as State)?.totalCount || 0,
  );

  const userEmail = useSelector(selectSwitchedUser);
  const customerPartnerData = useSelector<State, MACPartnerDataType['organisationDetails']>(
    (state) => selectGetCustomerPartnerId(state, userEmail)?.organisationDetails,
  );

  const customerPartnerId = customerPartnerData?.id;
  const customerIsPartner = customerPartnerData?.partner;

  useEffect(() => {
    if (userEmail) {
      dispatch(fetchGetCustomerByEmail(userEmail));
    }
  }, [userEmail]);

  useEffect(() => {
    if (customerPartnerId && customerIsPartner) {
      if (cartEnableCCPSearchCall) {
        dispatch(fetchPartnerWhitelistStatus(customerPartnerId));
      }

      setSearchData({ partnerId: customerPartnerId });
    }
  }, [customerPartnerId, customerIsPartner, cartEnableCCPSearchCall]);

  useEffect(() => {
    if (totalResults > 0) {
      setPagination({
        ...pagination,
        totalItems: totalResults,
      });
    }
  }, [totalResults]);

  const handlePaginationChange = useCallback(
    (params: { offset?: number }) => {
      setPagination({
        ...pagination,
        offset: params.offset || 0,
      });

      dispatch(
        fetchCustomerAccountOverviews(searchData, pagination.maxResult, params.offset || 0, true),
      );
    },
    [searchData, pagination.maxResult],
  );

  useEffect(() => {
    if (searchData.partnerId) {
      handlePaginationChange({ offset: 0 });
    }
  }, [searchData.partnerId, handlePaginationChange]);

  return (
    <LicensesPage
      {...props}
      partnerData={customerPartnerData}
      pagination={pagination}
      onPaginationChange={handlePaginationChange}
      updateSearchParams={setSearchData}
      searchParams={searchData}
      cartEnableCCPSearchCallFGOn={cartEnableCCPSearchCall}
    />
  );
};
